import React, { FocusEvent, ClipboardEvent, FormEvent, KeyboardEvent, PropsWithChildren, ReactElement, useEffect, useRef, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';

import './PreApprovedZipCode.css';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import {
    acquisitionFieldProps,
    getFormData,
    PADEFields,
    TAcquisitionFormData,
} from '../../types/shared/PreApprovedDataEntry';
import { Icon as TIcon } from '../../types/shared/Icon';
import { LinkWrapper } from '../LinkWrapper/LinkWrapper';
import { getParameterByName, wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { IPreApprovedLookupResponse } from '../../types/responses/PreApprovedLookup';
import { usePushdownContext } from '../../providers/Pushdown/PushdownProvider';
import SessionStorage, { clearAquisitionData } from '../../utils/SessionStorage';
import { acquisitionApiRequest, AcquisitionInitResponse } from '../../utils/acquisition-api/AcquisitionApi';
import { flowDataModule } from '../../utils/flowDataModule';
import { clearSessionAndRedirectToDataEntryPage, getMarketingAttributesForAcquisitions } from '../../utils/acquisition-utils';
import { useSessionStorageStatus } from '../../providers/SessionStorageStatus/SessionStorageStatusProvider';
import useSessionStorageCheck from '../../hooks/useSessionStorageCheck';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { useWidthContext } from '../../providers/Width';
import { InputBase } from 'component-library/index';
import ZipCodeField from '../FieldContainers/AddressFields/ZipCodeField/ZipCodeField';

import { Button, Title, Icon, Label } from '../../importCL';

interface IHelpListProps {
    description?: string;
    linkText?: string;
    link?: string;
}

interface IPreApprovedZipCodeProps extends MappedComponentProperties {
    skipPa?: boolean;
    splitInput?:boolean;
    specIds?: string[];
    title?: string;
    approvalCodeFieldTitle?: string;
    zipCodeFieldTitle?: string;
    ctaText?: string;
    helpSectionTitle?: string;
    helpListIcon?: TIcon;
    helpList?: IHelpListProps[];
    serverConnectionLostErrorCode?: string;
    noMatchErrorCode?: string;
    exceededAttemptsErrorCode?: string;
    solicitationInProgressErrorCode?: string;
    unrecognizedFieldErrorCode?: string;
    exceededAttemptsInSessionErrorCode?: string;
    processingExpiredOfferErrorCode?: string;
    expiredOfferUrl?: string;
    defaultErrorCode?: string;
    nextPage?: string;
}

export const PreApprovedZipCodeEditConfig = {
    emptyLabel: 'Pre Approved Zip Code',
    isEmpty: (props: PropsWithChildren<IPreApprovedZipCodeProps>) => {
        return !props || !props.title;
    },
};

const PreApprovedZipCode = (props: PropsWithChildren<IPreApprovedZipCodeProps>): JSX.Element => {
    const approvalCodeRef = useRef<HTMLInputElement>(null);
    const { width } = useWidthContext();
    const paSourceIdOverride = JSON.parse(PathUtils.getMetaPropertyValue('sourceIdOverrides') || '{}');
    const { showPushdown } = usePushdownContext();
    const apcId = getParameterByName('ApcID');
    const zip = getParameterByName('zip');
    const [isZipCodeInvalid, setIsZipCodeInvalid] = useState(false);
    const [isApprovalCodeInvalid, setIsApprovalCodeInvalid] = useState(false);

    const [formData, setFormData] = useState<Record<string, TAcquisitionFormData>>(() => {
        const data = getFormData(PADEFields);
        if (apcId) {
            data.approvalCode.value = apcId;
        }
        if (zip) {
            data.zipCode.value = zip;
        }

        return data;
    });

    //AB test: Split approval code---------------------------------------------------
    const approvalRef1 = useRef<HTMLInputElement>(null);
    const approvalRef2 = useRef<HTMLInputElement>(null);
    const approvalRef3 = useRef<HTMLInputElement>(null);

    const cleanedApc = apcId?.replace(/[^0-9]/g, '');
    const [approval1, setApproval1] = useState<string>(cleanedApc?.slice(0, 3) ?? '');
    const [approval2, setApproval2] = useState<string>(cleanedApc?.slice(3, 7) ?? '');
    const [approval3, setApproval3] = useState<string>(cleanedApc?.slice(7, 10) ?? '');
    const [approvalVisited, setApprovalVisited] = useState<boolean>(!!cleanedApc);

    const approvalSegmentsLengths = [ 3, 4, 3 ];
    const approvalSegmentsRefs= [approvalRef1, approvalRef2, approvalRef3];
    //--------------------------------------------------------------------------------
    const isShowingTitle = !paSourceIdOverride?.progressBarOverride || width >= 600;
    const isSubmitDisabled = !acquisitionFieldProps.approvalCode.isValid(formData.approvalCode.value) || !acquisitionFieldProps.zipCode.isValid(formData.zipCode.value) || isApprovalCodeInvalid || isZipCodeInvalid;
    const { status } = useSessionStorageStatus();
    useSessionStorageCheck();

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        const eventKey = event.key;
        if (eventKey === 'Enter') {
            event.preventDefault();
        }
    };

    //AB test: Split approval code---------------------------------------------------
    const updateApprovalCode = (code1: string, code2: string, code3: string): void => {
        const fullCode = code1 + code2 + code3;
        setApproval1(code1);
        setApproval2(code2);
        setApproval3(code3);

        setFormData(prev => ({
            ...prev,
            approvalCode: {...prev.approvalCode, value: fullCode}
        }))   
    }

    const handleApprovalChange = (index: number, value: string) : void => {
        const values = [approval1, approval2, approval3]
        if (value === values[index]) {
            return;
        }
        values[index] =value;
        updateApprovalCode(values[0], values[1], values[2])

        if (value.length === approvalSegmentsLengths[index] && document.activeElement === approvalSegmentsRefs[index].current) {
            approvalSegmentsRefs[index + 1]?.current?.focus();
        }
    };

    const handleApprovalKeyDown = (event: KeyboardEvent<HTMLInputElement>, prevElement?: React.MutableRefObject<HTMLInputElement>): void => {
        handleKeyDown(event);
        if (prevElement === undefined) {
            return;
        }

        const currentElement = event.target as HTMLInputElement;
        const previousElement = prevElement.current;

        if (previousElement && event.key === 'Backspace' && currentElement?.selectionStart === 0 && (currentElement?.selectionStart === currentElement?.selectionEnd)) {
            previousElement.focus();

            setTimeout(() => previousElement.setSelectionRange(previousElement.value.length, previousElement.value.length), 0)
        }
    };

    const handleApprovalPaste = (event: ClipboardEvent<HTMLInputElement>, index: number): void => {
        let pastedCode = event.clipboardData.getData('text').replace(/\D/g, '');

        if (!pastedCode) {
            return;
        }
        event.preventDefault();

        const values = [ approval1, approval2, approval3];
        const startField = pastedCode.length >= 10 ? 0 : index;
        let lastField;

        for (let i=startField; i< values.length && pastedCode; i++) {
            values[i] = pastedCode.slice(0, approvalSegmentsLengths[i])
            pastedCode = pastedCode.slice(approvalSegmentsLengths[i])
            lastField =i
        }

        updateApprovalCode(values[0], values[1], values[2]);
        
        const isLastFull = values[lastField].length === approvalSegmentsLengths[lastField];
        approvalSegmentsRefs[isLastFull ? Math.min(lastField + 1, 2) : lastField].current.focus();
    };

    const handleApprovalBlur = (event: FocusEvent<HTMLInputElement>):void  => {
        if (!approvalSegmentsRefs.some(ref => ref.current === event.relatedTarget)) {
            setApprovalVisited(true);
        }
    }

    const handleApprovalFocus = ():void  => {
        setApprovalVisited(false);
        setFormData(prev => ({
            ...prev,
            approvalCode: {...prev.approvalCode, hasBeenInteractedWith: true}
        }))
    }
    const isApprovalCodeError = approvalVisited && !acquisitionFieldProps.approvalCode.isValid(formData.approvalCode.value);
    //--------------------------------------------------------------------------------------

    const clearInputFields = () => {
        setIsApprovalCodeInvalid(false);
        setIsZipCodeInvalid(false);
        setFormData({
            ...formData,
            approvalCode: { ...formData.approvalCode, value: '' },
            zipCode: { ...formData.zipCode, value: '' },
        });

        setApproval1('');
        setApproval2('');
        setApproval3('')
        setApprovalVisited(false)
    };

    const initApi = async () => {
        if (!SessionStorage.getObject('adobeMode')) {
            try {
                const response = await acquisitionApiRequest<AcquisitionInitResponse>('init-compare-api', {});
                SessionStorage.setObject('initResponse', response.data);
            } catch {
                console.error('Error initializing acquisition api');
            }
        }
        return;
    };

    const handleResponse = async (response: { status: number, data: IPreApprovedLookupResponse }) => {
        await flowDataModule.setInSession({
            ...response.data.AdditionalInformation,
            Cards: response.data.CardDesign?.Cards || [],
            ErrorCodes: {
                nomatch: props.noMatchErrorCode,
                exceededattempts: props.exceededAttemptsErrorCode,
                inprogress: props.solicitationInProgressErrorCode,
                exceededAttemptsInSession: props.exceededAttemptsInSessionErrorCode,
                unrecognizedField: props.unrecognizedFieldErrorCode,
                processingExpiredOffer: props.processingExpiredOfferErrorCode,
                default: props.defaultErrorCode,
            },
        });

        switch (response.data.Result) {
            case 0: {
            //UnableToFindMatch
                showPushdown(props.noMatchErrorCode as string);
                setIsApprovalCodeInvalid(true);
                setIsZipCodeInvalid(true);
                break;
            }
            case 1: {
            //ExceededAttempts
                showPushdown(props.exceededAttemptsErrorCode as string);
                clearInputFields();
                break;
            }
            case 2: {
            //AlreadyInProgress
                showPushdown(props.solicitationInProgressErrorCode as string);
                clearInputFields();
                break;
            }
            case 3: {
            //Successful
                if (response.data.Applicant && response.data.CardDesign && response.data.CardDesign.Cards) {
                    SessionStorage.setObject('cardInfo', response.data.CardDesign.Cards);
                }

                SessionStorage.setCipherWithCurrentScope('pre-approval-data', response.data).catch(e => {
                    console.error('Error setting pre approval data', e);
                }).finally(() => {
                    SessionStorage.set('selectedProgramCode', response.data.ReservationNumber.substring(0, 4));
                    SessionStorage.set('approvalCode', response.data.ReservationNumber);

                    if (props.nextPage) {
                        window.location.assign(`${props.nextPage}${window.location.search}`);
                    } else {
                        clearSessionAndRedirectToDataEntryPage('lost', undefined, 'No next page redirect found');
                    }
                });

                break;
            }
            case 4: {
            //InvalidCodeOrSsn
                showPushdown(props.unrecognizedFieldErrorCode as string);
                clearInputFields();
                break;
            }
            case 5: {
            //ExceededAttemptsInSession
                showPushdown(props.exceededAttemptsInSessionErrorCode as string);
                clearInputFields();
                break;
            }
            case 9: {
            //Expired offer
                showPushdown(props.processingExpiredOfferErrorCode as string);
                clearInputFields();
                break;
            }
            case 10: {
            //Max Attempts in 24hrs
                showPushdown(props.serverConnectionLostErrorCode as string);
                clearInputFields();
                break;
            }
            default: {
                showPushdown(props.defaultErrorCode as string);
                clearInputFields();
            }
        }
    };

    const handleSubmit = async (e?: FormEvent<HTMLFormElement>) => {
        e?.preventDefault();

        const requestBody = {
            ApprovalCode: formData.approvalCode.value.replace(/\D/g, ''),
            LookupData: formData.zipCode.value,
            LookupDataType: 2, // SSN: 0, LAST_NAME: 1, ZIP_CODE: 2, SSN_LAST_FOUR_DIGITS: 3
            MarketingAttributes: getMarketingAttributesForAcquisitions(),
        };

        if (!SessionStorage.verifyNotEmpty('initResponse')) {
            await initApi();
        }

        acquisitionApiRequest<IPreApprovedLookupResponse>(
            'pre-approved-lookup',
            {
                method: 'post',
                body: requestBody,
            },
        ).then(async (response) => {
            if (response.data.Result === undefined
                    || response.data.Result === null
                    || response.status !== 200) {
                throw {
                    response: response,
                };
            } else {
                await handleResponse(response);
            }
        }).catch(async (error) => {
            if (error?.response.status === 408) {
                clearAquisitionData();
                const currentDataEntryPage = new URL(window.location.href);
                currentDataEntryPage.searchParams.set('errorCode', props.serverConnectionLostErrorCode as string);
                window.location.assign(currentDataEntryPage.toString());
            } else if (error?.response?.data?.Result !== undefined && error.response.data.Result !== null) {
                await handleResponse(error.response);
            } else {
                showPushdown(props.serverConnectionLostErrorCode as string);
                clearInputFields();
            }
        });

        
    };

    // handle SkipPA
    useEffect(() => {
        if (status === 'usable' && props.skipPa) {
            const error = getParameterByName('ApcError');
            const specIdCheck = props.specIds.length > 0 ? props.specIds.find((element) => element === getParameterByName('C1BSpecificationID')) !== undefined :  getParameterByName('C1BSpecificationID');
            const isFormComplete = !!(getParameterByName('ApcID') && getParameterByName('zip') && specIdCheck && props.nextPage);
            const currentParams = new URLSearchParams(window.location.search);

            if (error) {
                currentParams.delete('ApcError');
                const updatatedParamStr = currentParams.toString() ? '?' + currentParams.toString() : '';
                SessionStorage.set('queryParams', updatatedParamStr);
                window.history.pushState(null, '', window.location.pathname + updatatedParamStr );
            }

            if (isFormComplete && !error) {
                SessionStorage.set('skipPa', 'true');
                flowDataModule.setInSession({
                    ErrorCodes: {
                        nomatch: props.noMatchErrorCode,
                        exceededattempts: props.exceededAttemptsErrorCode,
                        inprogress: props.solicitationInProgressErrorCode,
                        exceededAttemptsInSession: props.exceededAttemptsInSessionErrorCode,
                        unrecognizedField: props.unrecognizedFieldErrorCode,
                        processingExpiredOffer: props.processingExpiredOfferErrorCode,
                        default: props.defaultErrorCode,
                    },
                }).then(() => {
                    window.location.assign(`${props.nextPage}?${currentParams.toString()}`);
                });
            }
        }
    }, [status]);

    return (
        <div id='pre-approved-zip-code' className='PreApprovedZipCode'>
            <form className='PreApprovedZipCode__fieldsSection' onSubmit={(e) => handleSubmit(e)}>
                {isShowingTitle && <p className='PreApprovedZipCode__fieldsSectionTitle'>{transformHTMLParserLinks(props.title ?? '')}</p>}
                    <div className='PreApprovedZipCode__inputContainer'>
                    {props.splitInput ? 
                        <div className='mb-4'> 
                            <Label>{props.approvalCodeFieldTitle}</Label>
                            <div className='flex items-center gap-4'>
                                <InputBase
                                    {...acquisitionFieldProps.approvalCode}
                                    labelContainerClassName='w-3/12'
                                    id='ApprovalCode-1'
                                    name='approvalCode1'
                                    ref={approvalRef1}
                                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleApprovalKeyDown(e)}
                                    onPaste={(e: ClipboardEvent<HTMLInputElement>) => handleApprovalPaste(e, 0)}
                                    onBlur={(e: FocusEvent<HTMLInputElement>) => handleApprovalBlur(e)}
                                    onFocus={handleApprovalFocus}
                                    value={approval1}
                                    setValue={(val: string) => {
                                        handleApprovalChange(0, val)
                                    }}                                
                                    maxLength={3}
                                    inputMode='numeric'
                                    required={false}
                                    alternate
                                    error={isApprovalCodeError}                                    
                                    hideAlertIcon
                                    externalValidation
                                    noErrorsOrHelper
                                    requiredMessage={acquisitionFieldProps.approvalCode.invalidErrorMessage}
                                    invalidErrorMessage={acquisitionFieldProps.approvalCode.invalidErrorMessage}
                                    valid={undefined}
                                    mask='000'
                                    useSuccessIcon={false}
                                    showSuccessIcon={false}
                                />
                                <InputBase
                                    {...acquisitionFieldProps.approvalCode}
                                    labelContainerClassName='w-5/12'
                                    id='ApprovalCode-2'
                                    name='approvalCode2'
                                    ref={approvalRef2}
                                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleApprovalKeyDown(e, approvalRef1)}
                                    onPaste={(e: ClipboardEvent<HTMLInputElement>) => handleApprovalPaste(e, 1)}
                                    onBlur={(e: FocusEvent<HTMLInputElement>) => handleApprovalBlur(e)}
                                    onFocus={handleApprovalFocus}
                                    value={approval2}
                                    setValue={(val: string) => {
                                        handleApprovalChange(1, val)
                                    }}                                
                                    maxLength={4}
                                    inputMode='numeric'
                                    required={false}
                                    alternate
                                    error={isApprovalCodeError}                                    
                                    hideAlertIcon
                                    externalValidation
                                    noErrorsOrHelper
                                    requiredMessage={acquisitionFieldProps.approvalCode.invalidErrorMessage}
                                    invalidErrorMessage={acquisitionFieldProps.approvalCode.invalidErrorMessage}
                                    valid={undefined}
                                    mask='0000'
                                    useSuccessIcon={false}
                                    showSuccessIcon={false}
                                />
                                <InputBase
                                    {...acquisitionFieldProps.approvalCode}
                                    dynamicContainerClassName='PreApprovedZipCode__splitApprovalCodeInput w-4/12'
                                    id='ApprovalCode-3'
                                    name='approvalCode3'
                                    ref={approvalRef3}
                                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleApprovalKeyDown(e, approvalRef2)}
                                    onPaste={(e: ClipboardEvent<HTMLInputElement>) => handleApprovalPaste(e, 2)}
                                    onBlur={(e: FocusEvent<HTMLInputElement>) => handleApprovalBlur(e)}
                                    onFocus={handleApprovalFocus}
                                    value={approval3}
                                    setValue={(val: string) => {
                                        handleApprovalChange(2, val)
                                    }}                                
                                    maxLength={3}
                                    inputMode='numeric'
                                    required={false}
                                    alternate
                                    error={isApprovalCodeError}                                                                
                                    hideAlertIcon
                                    externalValidation
                                    noErrorsOrHelper
                                    requiredMessage={acquisitionFieldProps.approvalCode.invalidErrorMessage}
                                    invalidErrorMessage={acquisitionFieldProps.approvalCode.invalidErrorMessage}
                                    valid={undefined}
                                    mask='000'
                                    useSuccessIcon
                                    showSuccessIcon={!((approvalVisited || apcId) && !acquisitionFieldProps.approvalCode.isValid(formData.approvalCode.value))}
                                />
                            </div> 
                            {isApprovalCodeError && (
                                <p className='InputBase__description--errorWithExclamation' aria-live='assertive'>
                                    {acquisitionFieldProps.approvalCode.invalidErrorMessage}
                                </p>
                            )}
                        </div>

                    :
                        <InputBase
                            {...acquisitionFieldProps.approvalCode}
                            className='PreApprovedZipCode__approvalCodeInput'
                            label={props.approvalCodeFieldTitle}
                            ref={approvalCodeRef}
                            onKeyDown={handleKeyDown}
                            value={formData.approvalCode.value}
                            setValue={(val: string) => {
                                setFormData({
                                    ...formData,
                                    approvalCode: { ...formData.approvalCode, value: val },
                                });
                                setIsApprovalCodeInvalid(false);
                            }}
                            alternate
                            maxLength={13}
                            inputMode='numeric'
                            required
                            removeAsterisk
                            requiredMessage={acquisitionFieldProps.approvalCode.invalidErrorMessage}
                            invalidErrorMessage={acquisitionFieldProps.approvalCode.invalidErrorMessage}
                            onFocus={() => {
                                setFormData({
                                    ...formData,
                                    approvalCode: { ...formData.approvalCode, hasBeenInteractedWith: true },
                                });
                            }}
                            mask='000-0000-000'
                            useSuccessIcon
                            showExclamationError
                            showSuccessIcon={!isApprovalCodeInvalid && acquisitionFieldProps.approvalCode.isValid(formData.approvalCode.value) && formData.approvalCode.hasBeenInteractedWith}
                            error={isApprovalCodeInvalid}
                            errorText='Check the code on your offer and try again'                        
                        />
                        }
                    </div>
                <div className='PreApprovedZipCode__inputContainer'>
                    <ZipCodeField
                        {...acquisitionFieldProps.zipCode}
                        className='PreApprovedZipCode__zipCodeInput'
                        label={props.zipCodeFieldTitle}
                        value={formData.zipCode.value}
                        setValueCallback={(val:string) => {
                            setFormData({
                                ...formData,
                                zipCode: { ...formData.zipCode, value: val },
                            });
                            setIsZipCodeInvalid(false);
                        }}
                        onFocusCallback={() => {
                            setFormData({
                                ...formData,
                                zipCode: { ...formData.zipCode, hasBeenInteractedWith: true },
                            });
                        }}
                        alternate
                        required
                        removeAsterisk
                        useSuccessIcon
                        invalidErrorMessage={acquisitionFieldProps.zipCode.invalidErrorMessage}
                        requiredMessage={acquisitionFieldProps.zipCode.invalidErrorMessage}
                        showSuccessIcon={!isZipCodeInvalid && acquisitionFieldProps.zipCode.isValid(formData.zipCode.value) && formData.zipCode.hasBeenInteractedWith}
                        error={isZipCodeInvalid}
                        errorText='Check your ZIP Code and try again'
                    />
                </div>
                <div className='PreApprovedZipCode__submitContainer'>
                    <Button
                        className='PreApprovedZipCode__submit'
                        type='submit'
                        id='data-entry-continue'
                        disabled={isSubmitDisabled || status !== 'usable'}>
                        <>
                            {props.ctaText ?? 'Submit'}
                        </>
                    </Button>
                </div>
            </form>
            <div className='PreApprovedZipCode__needHelpSection'>
                <Title className='PreApprovedZipCode__needHelpTitle' tag='h3' >
                    {transformHTMLParserLinks(props.helpSectionTitle ?? '')}
                </Title>
                {props.helpList && props.helpList.length > 0 && props.helpList.map((helpItem, idx) => {
                    return (
                        <div key={idx} className='PreApprovedZipCode__needHelpItem row'>
                            <Icon size='utility' src={props.helpListIcon?.iconPath} alt={props.helpListIcon?.iconAltText} />
                            <div className='PreApprovedZipCode__needHelpItemContent col'>
                                <div className='row'>{transformHTMLParserLinks(wrapTrademarkSuperscript(helpItem.description))}</div>
                                <LinkWrapper className='PreApprovedZipCode__needHelpItemLink row' href={helpItem.link}>
                                    <Button size='small' variant='link'>{transformHTMLParserLinks(wrapTrademarkSuperscript(helpItem.linkText)) as unknown as ReactElement}</Button>
                                </LinkWrapper>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PreApprovedZipCode;

